import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';

const LOGICALUNIT_URL = 'U3301';

const LogicalUnitEndpoints = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  EVENT_SEARCH: `${LOGICALUNIT_URL}/event/search`,
  EVENT_SEARCH_NAVIGATE: `${LOGICALUNIT_URL}/event/search/navigate`,
  EVENT_CREATE: `${LOGICALUNIT_URL}/event/create`,
  EVENT_UPDATE: (id: string) => `${LOGICALUNIT_URL}/event/update/${id}`,
  EVENT_DELETE: (id: string) => `${LOGICALUNIT_URL}/event/delete/${id}`,
  EVENT_MOVE: (id: string) => `${LOGICALUNIT_URL}/event/move/${id}`,
};

export type U3301CategoryOrigin = 'SYSTEM' | 'MANUAL';
export type U3301EventOrigin = 'MANUAL' | 'LINKED';
export type U3301CategoryDateMode = 'SINGLE' | 'INTERVAL';
export type U3301EventDeleteScope = 'DAY' | 'SERIES';

export interface U3301InitLUCategory {
  id: string;
  code: string;
  label: string;
  effectiveColorCode: string;
  origin: U3301CategoryOrigin;
  editableInCalendar: boolean;
  active: boolean;
  dateMode: U3301CategoryDateMode;
  generateWeekendEvents: boolean;
}

export interface U3301InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  categoryOrigins: U3301CategoryOrigin[];
  eventOrigins: U3301EventOrigin[];
  categories: U3301InitLUCategory[];
}

export interface U3301EventSearchPayload {
  dateFrom: string;
  dateTo: string;
  categoryIds?: string[];
  searchText?: string;
}

export interface U3301EventSearchNavigatePayload {
  searchText: string;
  categoryIds?: string[];
  currentEventId?: string;
}

export interface U3301EventSearchNavigateNeighbor {
  id: string;
  eventDate: string;
  title: string;
}

export interface U3301EventSearchNavigateResult {
  searchText: string;
  totalMatches: number;
  current: U3301EventSearchRow | null;
  hasPrevious: boolean;
  hasNext: boolean;
  /** Occurrences strictement avant le courant (tri canonique). */
  previousCount: number;
  /** Occurrences strictement après le courant (tri canonique). */
  nextCount: number;
  previous: U3301EventSearchNavigateNeighbor | null;
  next: U3301EventSearchNavigateNeighbor | null;
}

/** Position 1-based dans la chaîne : previousCount + 1 (0 si pas de courant). */
export function u3301SearchNavigateCurrentIndex(previousCount: number, hasCurrent: boolean): number {
  if (!hasCurrent) return 0;
  return Math.max(0, previousCount) + 1;
}

/** Construit le body navigate loupe (trim searchText). */
export function buildEventSearchNavigateBody(
  payload: U3301EventSearchNavigatePayload,
): U3301EventSearchNavigatePayload {
  const body: U3301EventSearchNavigatePayload = {
    searchText: payload.searchText.trim(),
  };
  if (payload.categoryIds && payload.categoryIds.length > 0) {
    body.categoryIds = payload.categoryIds;
  }
  if (payload.currentEventId) {
    body.currentEventId = payload.currentEventId;
  }
  return body;
}

export interface U3301EventSearchCategory {
  id: string;
  code: string;
  label: string;
  colorCode: string;
  effectiveColorCode: string;
  origin: U3301CategoryOrigin;
  editableInCalendar: boolean;
}

export interface U3301EventSearchRow {
  id: string;
  title: string;
  description?: string | null;
  eventDate: string;
  allDay: boolean;
  origin: U3301EventOrigin;
  projectNumber?: string | null;
  version: number;
  seriesId?: string | null;
  category: U3301EventSearchCategory;
}

export type U3301EventCreateBody =
  | {
      categoryId: string;
      title: string;
      description?: string | null;
      eventDate: string;
    }
  | {
      categoryId: string;
      title: string;
      description?: string | null;
      startDate: string;
      endDate: string;
    };

export interface U3301EventUpdateBody {
  id: string;
  version: number;
  title: string;
  description?: string | null;
  eventDate: string;
  categoryId?: string;
}

export interface U3301EventDeleteBody {
  id: string;
  version: number;
  scope?: U3301EventDeleteScope;
}

export interface U3301EventMoveBody {
  eventDate: string;
  version: number;
}

export type U3301EventCreatePayload =
  | {
      categoryId: string;
      title: string;
      description: string;
      eventDate: string;
    }
  | {
      categoryId: string;
      title: string;
      description: string;
      startDate: string;
      endDate: string;
    };

export function buildEventCreateBody(payload: U3301EventCreatePayload): U3301EventCreateBody {
  const base = {
    categoryId: payload.categoryId,
    title: payload.title.trim(),
    description: payload.description.trim() === '' ? null : payload.description.trim(),
  };
  if ('startDate' in payload && 'endDate' in payload) {
    return {
      ...base,
      startDate: payload.startDate,
      endDate: payload.endDate,
    };
  }
  return {
    ...base,
    eventDate: payload.eventDate,
  };
}

export function buildEventUpdateBody(payload: {
  id: string;
  version: number;
  title: string;
  description: string;
  eventDate: string;
  categoryId?: string;
}): U3301EventUpdateBody {
  const body: U3301EventUpdateBody = {
    id: payload.id,
    version: payload.version,
    title: payload.title.trim(),
    description: payload.description.trim() === '' ? null : payload.description.trim(),
    eventDate: payload.eventDate,
  };
  if (payload.categoryId) {
    body.categoryId = payload.categoryId;
  }
  return body;
}

export function buildEventDeleteBody(payload: {
  id: string;
  version: number;
  scope?: U3301EventDeleteScope;
}): U3301EventDeleteBody {
  const body: U3301EventDeleteBody = {
    id: payload.id,
    version: payload.version,
  };
  if (payload.scope) {
    body.scope = payload.scope;
  }
  return body;
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearchEvents = (axios: AxiosInstance, body: U3301EventSearchPayload) => {
  return axios.post(LogicalUnitEndpoints.EVENT_SEARCH, body);
};

export const doNavigateSearchEvents = (
  axios: AxiosInstance,
  body: U3301EventSearchNavigatePayload,
) => {
  return axios.post(LogicalUnitEndpoints.EVENT_SEARCH_NAVIGATE, body);
};

export const doCreateEvent = (axios: AxiosInstance, body: U3301EventCreateBody) => {
  return axios.post(LogicalUnitEndpoints.EVENT_CREATE, body);
};

export const doUpdateEvent = (axios: AxiosInstance, id: string, body: U3301EventUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.EVENT_UPDATE(id), body);
};

export const doDeleteEvent = (axios: AxiosInstance, body: U3301EventDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.EVENT_DELETE(body.id), body);
};

export const doMoveEvent = (axios: AxiosInstance, id: string, body: U3301EventMoveBody) => {
  return axios.post(LogicalUnitEndpoints.EVENT_MOVE(id), body);
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: { errorCode?: string }, errorResult: U2000ApiErrorResult): void {
  if (err.errorCode === 'BU330114') {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: nameSpace }),
      text: t('errorBU330114', { ns: nameSpace }),
    });
  }
  if (err.errorCode === 'BU330106') {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: nameSpace }),
      text: t('errorBU330106', { ns: nameSpace }),
    });
  }
}
